/**
 * Authentication Service
 *
 * Service for all authentication-related API calls to auth-service
 * Endpoints based on Postman collection documentation
 */

import { setCurrentEstablishment, removeCurrentEstablishment } from '@utils/session';
import { apiClient } from './apiClient';
import type {
  ApiError,
  AuthUser,
  LoginRequest,
  LoginResponse,
  LogoutRequest,
  LogoutResponse,
  PinVerifyRequest,
  PinVerifyResponse,
  PinSetRequest,
  PinResetRequest,
  PinResetConfirmRequest,
  PinChangeRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  SessionsResponse,
  TokenData,
  UserInfoMembership,
  UserInfoOwnerOf,
  UserInfoProfile,
  UserInfoResponse,
  UserMeResponse,
} from './types';

export type CurrentUserProfile = {
  user_id: string;
  establishment_id: string;
  name: string;
  role: string;
  email: string;
  enable_withdrawn: boolean;
};

// Centralized authentication routes
const ROUTES = {
  LOGIN: '/auth/login',
  REFRESH: '/auth/refresh',
  ME: '/auth/me',
  USER_INFO: '/auth/userinfo',
  SESSIONS: '/auth/sessions',
  LOGOUT: '/auth/logout',
  LOGOUT_ALL: '/auth/logout/all',
  SELECT_ESTABLISHMENT: '/auth/select-establishment',
  SELECT_PROFILE: '/auth/select-profile',
  PIN_VERIFY: '/auth/pin/verify',
  PIN_SET: '/auth/pin/set',
  PIN_RESET_REQUEST: '/auth/pin/reset/request',
  PIN_RESET_CONFIRM: '/auth/pin/reset/confirm',
  PIN_CHANGE: '/auth/pin/change',
} as const;

class AuthService {
  /**
   * Login with email and password
   * POST /auth/login
   *
   * @param login - User email
   * @param senha - User password
   * @param channel - Access channel ('web' or 'app')
   * @returns Login response with tokens and user info
   */
  async login(login: string, senha: string, channel: 'web' | 'app' = 'web'): Promise<LoginResponse> {
    try {
      const loginData: LoginRequest = {
        login,
        senha,
        channel,
      };

      const response = await apiClient.post<LoginResponse>(ROUTES.LOGIN, loginData);

      // Store tokens after successful login
      if (response.access_token && response.refresh_token) {
        const tokenData: TokenData = {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          session_id: response.session_id,
          user_id: response.user_id,
        };

        apiClient.storeTokens(tokenData);
      }

      const userInfo = await this.getUserInfo();

      return response;
    } catch (error) {
      throw this.handleAuthError(error, 'Erro ao fazer login');
    }
  }

  /**
   * Refresh access token using refresh token
   * POST /auth/refresh
   *
   * @param refreshToken - Valid refresh token
   * @returns New access token and refresh token
   */
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    try {
      const refreshData: RefreshTokenRequest = {
        refresh_token: refreshToken,
      };

      const response = await apiClient.post<RefreshTokenResponse>(ROUTES.REFRESH, refreshData);

      // Update stored tokens
      if (response.access_token && response.refresh_token) {
        const tokenData: TokenData = {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          session_id: response.session_id,
          user_id: response.user_id,
        };

        apiClient.storeTokens(tokenData);
      }

      return response;
    } catch (error) {
      throw this.handleAuthError(error, 'Erro ao renovar token');
    }
  }

  /**
   * Get current user basic information (email only)
   * GET /auth/me (Protected route)
   *
   * @returns User email
   */
  async getMe(): Promise<UserMeResponse> {
    try {
      return await apiClient.get<UserMeResponse>(ROUTES.ME);
    } catch (error) {
      throw this.handleAuthError(error, 'Erro ao obter informações básicas do usuário');
    }
  }

  /**
   * Get current user complete information
   * GET /auth/userinfo (Protected route)
   *
   * @returns Complete user information
   */
  async getUserInfo(): Promise<UserInfoResponse> {
    try {
      const userInfo = await apiClient.get<UserInfoResponse>(ROUTES.USER_INFO);

      // Store user data in localStorage for offline access
      apiClient.storeUserData(userInfo);

      return userInfo;
    } catch (error) {
      throw this.handleAuthError(error, 'Erro ao obter informações do usuário');
    }
  }

  /**
   * Get all active sessions for current user
   * GET /auth/sessions (Protected route)
   *
   * @returns List of active sessions
   */
  async getSessions(): Promise<SessionsResponse> {
    try {
      return await apiClient.get<SessionsResponse>(ROUTES.SESSIONS);
    } catch (error) {
      throw this.handleAuthError(error, 'Erro ao obter sessões ativas');
    }
  }

  /**
   * Logout from specific session
   * POST /auth/logout (Protected route)
   *
   * @param sessionId - Session ID to logout from
   * @returns Logout confirmation
   */
  async logout(sessionId: string): Promise<LogoutResponse> {
    try {
      const logoutData: LogoutRequest = {
        session_id: sessionId,
      };

      const response = await apiClient.post<LogoutResponse>(ROUTES.LOGOUT, logoutData);

      // Note: Local auth clearing is handled by the logout() utility function
      // to avoid conflicts with navigation logic in components

      return response;
    } catch (error) {
      throw this.handleAuthError(error, 'Erro ao fazer logout');
    }
  }

  /**
   * Logout from all sessions
   * POST /auth/logout/all (Protected route)
   *
   * Note: Despite the endpoint expecting session_id in body according to Postman,
   * this should logout from ALL sessions, so we'll send current session_id
   *
   * @returns Logout confirmation
   */
  async logoutAll(): Promise<LogoutResponse> {
    try {
      const currentTokens = apiClient.getStoredTokens();
      if (!currentTokens?.session_id) {
        throw new Error('Nenhuma sessão ativa encontrada');
      }

      const logoutData: LogoutRequest = {
        session_id: currentTokens.session_id,
      };

      const response = await apiClient.post<LogoutResponse>(ROUTES.LOGOUT_ALL, logoutData);

      // Clear local tokens since all sessions are logged out
      this.clearLocalAuth();
      return response;
    } catch (error) {
      throw this.handleAuthError(error, 'Erro ao fazer logout de todas as sessões');
    }
  }

  /**
   * Seleciona o estabelecimento e recarrega a aplicação para limpar todo o estado
   * do estabelecimento anterior. Informe `redirectTo` para cair em outra rota
   * depois da troca (ex.: '/dashboard'); sem isso, recarrega a rota atual.
   */
  async postSelectEstablishment(establishmentId: string, redirectTo?: string): Promise<void> {
    try {
      await apiClient.post<void>(ROUTES.SELECT_ESTABLISHMENT, { establishment_id: establishmentId });
      await this.getUserInfo();
      // Usa getEstablishments() que retorna o formato correto incluindo memberships normalizados
      const foundEstablishment = this.getEstablishments().find((establishment) => establishment.id === establishmentId);
      if (foundEstablishment) {
        setCurrentEstablishment(foundEstablishment);
      }
      // Navegação via window (e não pelo router) é proposital: força um boot
      // completo da aplicação, garantindo que nenhum cache em memória do
      // estabelecimento anterior sobreviva à troca.
      if (redirectTo) {
        window.location.assign(redirectTo);
      } else {
        window.location.reload();
      }
    } catch (error) {
      throw this.handleAuthError(error, 'Erro ao selecionar estabelecimento');
    }
  }

  async postSelectProfile(profileId: string): Promise<void> {
    try {
      await apiClient.post<void>(ROUTES.SELECT_PROFILE, { profile_id: profileId });
      await this.getUserInfo();
      window.location.reload();
    } catch (error) {
      throw this.handleAuthError(error, 'Erro ao selecionar perfil');
    }
  }

  /**
   * Get current user data from stored tokens and API
   * Combines local token data with user info from API
   *
   * @returns Current authenticated user or null
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const tokens = apiClient.getStoredTokens();
      if (!tokens) {
        return null;
      }

      // Get user info from API
      const userInfo = await this.getUserInfo();

      return {
        id: userInfo.user_id,
        email: userInfo.email,
        name: userInfo.name,
        profile_ids: userInfo.profiles.map((profile) => profile.id),
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Check if user is currently authenticated
   *
   * @returns True if user has valid tokens
   */
  isAuthenticated(): boolean {
    return apiClient.isAuthenticated();
  }

  /**
   * Clear all local authentication data
   */
  clearLocalAuth(): void {
    apiClient.clearAuth();
  }

  /**
   * Get stored token data
   *
   * @returns Current token data or null
   */
  getStoredTokens(): TokenData | null {
    return apiClient.getStoredTokens();
  }

  /**
   * Get stored user data
   *
   * @returns Current user data or null
   */
  getStoredUserData(): UserInfoResponse | null {
    return apiClient.getStoredUserData();
  }

  getCurrentUserProfile(): CurrentUserProfile | null {
    const userData = apiClient.getStoredUserData();
    if (!userData) return null;
    const currentProfile = userData.profiles.find((profile) => profile.active);
    const currentEstablishment = this.getCurrentEstablishment();

    // owners sempre têm permissão; gerentes leem enable_withdrawn de memberships
    const isOwner = (userData.owner_of ?? []).some((est) => est.id === currentEstablishment?.id);
    const membership = (userData.memberships ?? []).find((m) => m.establishment_id === currentEstablishment?.id);
    const enableWithdrawn = isOwner ? true : (membership?.enable_withdrawn ?? false);

    // establishment_id e role saíam do perfil, que não carrega nenhum dos dois:
    // /auth/userinfo devolve ProfileLite {id, name, channel, active}. Na prática
    // o retorno era sempre establishment_id: '' e role: 'user'.
    // O estabelecimento vem da seleção corrente, e o papel dentro dele vem do
    // membership — caindo no nome do perfil quando não há vínculo (dono, por
    // exemplo, não aparece em memberships).
    const user: CurrentUserProfile = {
      user_id: userData.user_id,
      establishment_id: currentEstablishment?.id || '',
      name: userData.first_name || userData.email,
      role: membership?.role || currentProfile?.name || 'user',
      email: userData.email,
      enable_withdrawn: enableWithdrawn,
    };
    return user;
  }

  getProfiles(): { active: boolean; channel: string; id: string; name: string }[] | [] {
    const user = this.getStoredUserData();
    if (!user) return [];
    return (user.profiles as unknown as { active: boolean; channel: string; id: string; name: string }[]) || [];
  }

  getAllEstablishments(): any[] {
    const user = this.getStoredUserData();
    if (!user) return [];
    const establishments = new Map<string, { id: string; name: string; support_code: string }>();

    if (user?.owner_of) {
      user.owner_of.forEach((establishment) => {
        establishments.set(establishment.id, {
          id: establishment.id,
          name: establishment.name,
          support_code: establishment.support_code,
        });
      });
    }

    if (user?.memberships) {
      user.memberships.forEach((membership) => {
        if (establishments.has(membership.establishment_id)) return;
        establishments.set(membership.establishment_id, {
          id: membership.establishment_id,
          name: membership.establishment_name,
          support_code: membership.support_code || '',
        });
      });
    }

    return Array.from(establishments.values());
  }

  getEstablishments(): UserInfoOwnerOf[] {
    const user = this.getStoredUserData();
    if (!user) return [];

    const establishments = new Map<string, UserInfoOwnerOf>();

    // Adiciona estabelecimentos onde o usuário é owner
    if (user.owner_of && user.owner_of.length > 0) {
      user.owner_of.forEach((establishment) => {
        establishments.set(establishment.id, establishment);
      });
    }

    // Adiciona estabelecimentos onde o usuário é membro (gerente)
    if (user.memberships && user.memberships.length > 0) {
      user.memberships
        .filter((membership) => membership.active) // Apenas membros ativos
        .forEach((membership) => {
          if (establishments.has(membership.establishment_id)) return;

          establishments.set(membership.establishment_id, {
            id: membership.establishment_id,
            name: membership.establishment_name,
            institution_name: '', // Não disponível em memberships
            saldo_bloqueado: 0, // Não disponível em memberships
            saldo_disponivel: 0, // Não disponível em memberships
            support_code: membership.support_code || '',
          });
        });
    }

    return Array.from(establishments.values());
  }

  getCurrentEstablishment(): UserInfoOwnerOf | null {
    const establishment = localStorage.getItem('current_establishment');
    return establishment ? JSON.parse(establishment) : null;
  }

  needsProfileSelection(): boolean {
    const user = this.getStoredUserData();
    if (!user) return false;
    return user.needs_profile_selection;
  }

  needsEstablishmentSelection(): boolean {
    const establishment = this.getCurrentEstablishment();
    if (!establishment) return true;
    return false;
  }

  getEstabilishmentSupportCode(): string | null {
    const user = this.getStoredUserData();
    const currentEstablishment = this.getCurrentEstablishment();
    if (!currentEstablishment || !user) return null;

    // Busca o support_code do estabelecimento atual
    // Primeiro tenta em owner_of
    if (user.owner_of) {
      const ownerEstablishment = user.owner_of.find((est) => est.id === currentEstablishment.id);
      if (ownerEstablishment?.support_code) {
        return ownerEstablishment.support_code;
      }
    }

    // Se não encontrou em owner_of, tenta em memberships
    if (user.memberships) {
      const membership = user.memberships.find((m) => m.establishment_id === currentEstablishment.id);
      if (membership?.support_code) {
        return membership.support_code;
      }
    }

    return 'não encontrado';
  }

  /**
   * Verifica se o usuário é gerente (tem memberships mas não é owner)
   * @returns true se o usuário é apenas gerente (não é owner de nenhum estabelecimento)
   */
  isManagerOnly(): boolean {
    const user = this.getStoredUserData();
    if (!user) return false;

    // Se tem owner_of (mesmo que vazio), não é apenas gerente
    const hasOwnerOf = user.owner_of && user.owner_of.length > 0;

    // Se tem memberships ativos, é gerente
    const hasActiveMemberships = user.memberships && user.memberships.some((m) => m.active);

    // É gerente apenas se tem memberships ativos mas não é owner
    return hasActiveMemberships && !hasOwnerOf;
  }

  /**
   * Verifica se o usuário logado é owner do estabelecimento atual
   */
  isOwnerOfCurrentEstablishment(): boolean {
    const user = this.getStoredUserData();
    const currentEstablishment = this.getCurrentEstablishment();
    if (!user || !currentEstablishment) return false;
    return (user.owner_of ?? []).some((est) => est.id === currentEstablishment.id);
  }

  /**
   * Clear current establishment selection to allow re-selection
   * This removes the establishment from localStorage, allowing the user
   * to select a different establishment
   */
  clearEstablishmentSelection(): void {
    removeCurrentEstablishment();
  }

  getCurrentProfileId(): string | null {
    const user = this.getStoredUserData();
    if (!user) return null;
    return user.selected_profile_id || null;
  }

  getCurrentEstablishmentSaldoDisponivel(): number {
    const establishment = this.getCurrentEstablishment();
    return establishment?.saldo_disponivel || 0;
  }

  getCurrentEstablishmentSaldoBloqueado(): number {
    const establishment = this.getCurrentEstablishment();
    return establishment?.saldo_bloqueado || 0;
  }

  /**
   * Update tokens after profile activation
   *
   * @param access_token - New access token
   * @param session_id - New session ID
   * @returns Promise that resolves when tokens are updated and user info is refreshed
   */
  async updateTokensAfterProfileActivation(access_token: string, session_id: string): Promise<UserInfoResponse> {
    try {
      console.log('Atualizando tokens após ativação de perfil...');

      // Validate input parameters
      if (!access_token || !session_id) {
        throw new Error('Access token e session ID são obrigatórios');
      }

      // Get current tokens to preserve refresh_token and user_id
      const currentTokens = this.getStoredTokens();
      if (!currentTokens) {
        throw new Error('Nenhum token encontrado para atualizar');
      }

      console.log('Tokens atuais:', {
        hasRefreshToken: !!currentTokens.refresh_token,
        userId: currentTokens.user_id,
        oldSessionId: currentTokens.session_id,
      });

      // Update tokens with new access_token and session_id
      const newTokenData: TokenData = {
        access_token,
        refresh_token: currentTokens.refresh_token,
        session_id,
        user_id: currentTokens.user_id,
      };

      console.log('Novos tokens:', {
        hasAccessToken: !!newTokenData.access_token,
        hasRefreshToken: !!newTokenData.refresh_token,
        newSessionId: newTokenData.session_id,
        userId: newTokenData.user_id,
      });

      // Store updated tokens
      apiClient.storeTokens(newTokenData);
      console.log('Tokens armazenados com sucesso');

      // Fetch fresh user info with new tokens
      console.log('Buscando informações atualizadas do usuário...');
      const userInfo = await this.getUserInfo();
      console.log('User info obtido com sucesso:', userInfo);

      return userInfo;
    } catch (error) {
      console.error('Erro ao atualizar tokens após ativação de perfil:', error);
      throw this.handleAuthError(error, 'Erro ao atualizar tokens após ativação de perfil');
    }
  }

  /**
   * Verify financial PIN and obtain a single-use confirmation token
   * for a step-up operation (e.g. withdraw confirm).
   * POST /auth/pin/verify
   */
  async postPinVerify(body: PinVerifyRequest): Promise<PinVerifyResponse> {
    return apiClient.post<PinVerifyResponse>(ROUTES.PIN_VERIFY, body, { skipToast: true });
  }

  /**
   * Set financial PIN using the current user's password.
   * POST /auth/pin/set
   */
  async postPinSet(body: PinSetRequest): Promise<void> {
    await apiClient.post<void>(ROUTES.PIN_SET, body, { skipToast: true });
  }

  /**
   * Request PIN reset code by email.
   * POST /auth/pin/reset/request
   */
  async postPinResetRequest(body: PinResetRequest): Promise<void> {
    await apiClient.post<void>(ROUTES.PIN_RESET_REQUEST, body, { skipToast: true });
  }

  /**
   * Confirm PIN reset with e-mail code and new PIN.
   * POST /auth/pin/reset/confirm
   */
  async postPinResetConfirm(body: PinResetConfirmRequest): Promise<void> {
    await apiClient.post<void>(ROUTES.PIN_RESET_CONFIRM, body, { skipToast: true });
  }

  /**
   * Change financial PIN using current PIN.
   * POST /auth/pin/change
   */
  async postPinChange(body: PinChangeRequest): Promise<void> {
    await apiClient.post<void>(ROUTES.PIN_CHANGE, body, { skipToast: true });
  }

  /**
   * Handle authentication-specific errors
   *
   * @param error - Original error
   * @param defaultMessage - Default message if no specific error message
   * @returns Formatted error
   */
  private handleAuthError(error: unknown, defaultMessage: string): ApiError {
    if (error && typeof error === 'object' && 'message' in error) {
      const apiError = error as ApiError;

      // Handle specific HTTP status codes
      switch (apiError.status) {
        case 401:
          return {
            ...apiError,
            message: 'Credenciais inválidas ou sessão expirada',
          };
        case 403:
          return {
            ...apiError,
            message: 'Acesso negado',
          };
        case 404:
          return {
            ...apiError,
            message: 'Endpoint não encontrado',
          };
        case 422:
          return {
            ...apiError,
            message: 'Dados inválidos fornecidos',
          };
        case 429:
          return {
            ...apiError,
            message: 'Muitas tentativas. Tente novamente mais tarde',
          };
        case 500:
          return {
            ...apiError,
            message: 'Erro interno do servidor',
          };
        default:
          return {
            ...apiError,
            message: apiError.message || defaultMessage,
          };
      }
    }

    return {
      message: defaultMessage,
      status: 0,
    } as ApiError;
  }

  getUserInfoFromStorage(): UserInfoResponse | null {
    const userInfo = localStorage.getItem('cantina_pay_user_info');
    if (!userInfo) {
      return null;
    }
    return JSON.parse(userInfo);
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export class for testing
export { AuthService };
export default authService;
